import {
  AttachMoney,
  CalendarToday,
  CheckCircle,
  Cancel,
  DirectionsCar,
  LocalGasStation,
  LocationOn,
  Phone,
  Warning,
  Note as NoteIcon,
  Timeline as TimelineIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material'
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardMedia,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  ImageList,
  ImageListItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { alpha, type Theme } from '@mui/material/styles'
import { Vehicle } from '@otw/models/inventory'
import type { PickupLocation } from '@otw/models/transporter'
import type { DealApprovalStageType, VehicleDecision } from '@otw/models/deals'
import {
  getVehicleDecisionStatus,
  getStageDecision,
  getRejectedStage,
  getConditionallyApprovedStages,
} from '@otw/models/deals'
import {
  boxTruck as boxTruckIcon,
  cargoVan as cargoVanIcon,
  cutaway as cutawayIcon,
  stepVan as stepVanIcon,
} from 'assets'
import {
  formatDateTime,
  formatDistance,
  formatPhoneNumber,
  formatPrice,
  getVehicleStatusStyles,
} from 'core/utils/inventoryUtils'
import { StatusChip } from 'components/StatusChip'
import { type StatusTone } from 'core/theme.tokens'
import { useIsMobile } from 'hooks/screen/useIsMobile'
import { usePhotosForInventoryObject } from '@/hooks/inventory/usePhotosForInventoryObject'
import React, { useMemo, useState } from 'react'
import { TimelineDialog } from '@/views/SuperAdmin/Management/TransporterManagement/components/dialogs'
import ManagePhotosDialog from '@/components/inventory/dialogs/ManagePhotosDialog'
import VehicleEditButton from '@/components/adminDashboard/VehicleEditButton'
import dayjs from 'dayjs'
import { getDealQualityInfo } from 'hooks/graybook'
import { useUsers } from 'hooks/useUsers'

const cachedIcons = {
  'step van': stepVanIcon,
  'box truck': boxTruckIcon,
  'cargo van': cargoVanIcon,
  cutaway: cutawayIcon,
  default: stepVanIcon,
}

Object.values(cachedIcons).forEach(src => {
  const img = new Image()
  img.src = src
})

function formatPickupLocation(pl: PickupLocation | null | undefined): string {
  if (!pl) return ''
  const parts = [pl.address, [pl.city, pl.state, pl.zip].filter(Boolean).join(', ')].filter(Boolean)
  return parts.join(', ')
}

/** True if vehicle has a complete pickup location (address, city, state, zip all required to approve) */
export function vehicleHasPickupLocation(vehicle: Vehicle): boolean {
  const pl = vehicle.pickup_location
  if (!pl) return false
  const a = pl.address?.trim()
  const c = pl.city?.trim()
  const s = pl.state?.trim()
  const z = pl.zip?.trim()
  return !!(a && c && s && z)
}

const vehicleDecisionStatusTone = (
  status: ReturnType<typeof getVehicleDecisionStatus>
): StatusTone => {
  switch (status) {
    case 'approved':
      return 'success'
    case 'conditionally_approved':
      return 'warning'
    case 'rejected':
      return 'danger'
    case 'pending':
      return 'waiting'
    default:
      return 'neutral'
  }
}

const dealQualityStatusTone = (quality: NonNullable<Vehicle['dealQuality']>): StatusTone => {
  switch (quality) {
    case 'great':
      return 'success'
    case 'good':
      return 'info'
    case 'average':
      return 'neutral'
    case 'belowAverage':
      return 'warning'
    case 'poor':
      return 'danger'
    default:
      return 'neutral'
  }
}

interface DealApprovalVehicleCardProps {
  collectionName: 'master_inventory' | 'shield_inventory'
  vehicle: Vehicle
  canViewPurchasePrice?: boolean
  canViewProfit?: boolean
  vehicleDecision?: VehicleDecision
  canApprove?: boolean
  /** Stage the current user approves (so we can highlight "Your turn" on that stage) */
  currentUserStage?: DealApprovalStageType
  onApprove?: () => void
  onConditionalApprove?: () => void
  onReject?: () => void
  isUpdating?: boolean
}

const DealApprovalVehicleCard: React.FC<DealApprovalVehicleCardProps> = ({
  collectionName,
  vehicle,
  canViewPurchasePrice = false,
  canViewProfit = false,
  vehicleDecision,
  canApprove = false,
  currentUserStage,
  onApprove,
  onConditionalApprove,
  onReject,
  isUpdating = false,
}) => {
  const isMobile = useIsMobile()
  const { users } = useUsers()
  const approverNameByUserId = useMemo(() => {
    const map: Record<string, string> = {}
    for (const u of users) {
      const name = [u.first_name, u.last_name].filter(Boolean).join(' ').trim() || u.displayName || u.email || u.id
      map[u.id] = name || 'Unknown'
    }
    return map
  }, [users])
  const getApproverDisplayName = (userId: string | undefined) =>
    userId ? (approverNameByUserId[userId] ?? 'Unknown') : ''

  const [timelineDialogOpen, setTimelineDialogOpen] = useState(false)
  const [photosDialogOpen, setPhotosDialogOpen] = useState(false)
  const { data: photos, isLoading: isLoadingPhoto } = usePhotosForInventoryObject(collectionName, vehicle.id, {
    onlyMainPhoto: true,
    enabled: vehicle.photo_storage_path !== '',
  })
  const { data: allPhotos, isLoading: isLoadingAllPhotos } = usePhotosForInventoryObject(collectionName, vehicle.id, {
    onlyMainPhoto: false,
    enabled: vehicle.photo_storage_path !== '',
  })

  const statusColor = useMemo(() => {
    return getVehicleStatusStyles(vehicle.status).hex
  }, [vehicle.status])

  const dealQualityInfo = useMemo(() => {
    if (vehicle?.dealQuality) {
      const { label, color } = getDealQualityInfo(vehicle.dealQuality)
      return {
        label,
        color,
      }
    }
    return null
  }, [vehicle?.dealQuality])

  const hasPickupLocation = vehicleHasPickupLocation(vehicle)
  const pickupLocationText = formatPickupLocation(vehicle.pickup_location ?? undefined)

  const derivedStatus = getVehicleDecisionStatus(vehicleDecision)
  const decisionLabel =
    derivedStatus === 'conditionally_approved'
      ? 'Conditionally Approved'
      : derivedStatus === 'approved'
        ? 'Approved'
        : derivedStatus === 'rejected'
          ? 'Rejected'
          : derivedStatus === 'pending'
            ? 'In progress'
            : null

  const STAGE_DISPLAY_NAMES: Record<DealApprovalStageType, string> = {
    availability: 'Availability',
    condition: 'Condition',
    title: 'Title',
    final: 'Final',
  }

  const ApprovalStagesRow = () => {
    const stages: DealApprovalStageType[] = ['availability', 'condition', 'title', 'final']
    return (
      <Box
        sx={{
          p: 1.5,
          borderRadius: 1.5,
          bgcolor: 'action.hover',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography
          variant='caption'
          color='text.secondary'
          sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600, display: 'block', mb: 1 }}
        >
          Approval stages
        </Typography>
        <Stack spacing={1}>
          {stages.map(stage => {
            const stageDec = getStageDecision(vehicleDecision, stage)
            const decided = Boolean(stageDec?.user_id && stageDec?.status)
            const approved = decided && (stageDec!.status === 'approved' || stageDec!.status === 'conditionally_approved')
            const conditionallyApproved = decided && stageDec!.status === 'conditionally_approved'
            const rejectedAtThisStage = decided && stageDec!.status === 'rejected'
            const label = STAGE_DISPLAY_NAMES[stage]
            const approverName = getApproverDisplayName(stageDec?.user_id)
            const isYourTurn = canApprove && currentUserStage === stage && !decided
            return (
              <Box
                key={stage}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  py: 0.5,
                  px: 1,
                  borderRadius: 1,
                  ...(isYourTurn && {
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    '& .MuiTypography-root': { color: 'inherit' },
                  }),
                }}
              >
                {approved ? (
                  <CheckCircle
                    sx={{
                      fontSize: 18,
                      color: conditionallyApproved ? 'warning.main' : 'success.main',
                      flexShrink: 0,
                    }}
                  />
                ) : rejectedAtThisStage ? (
                  <Cancel sx={{ fontSize: 18, color: 'error.main', flexShrink: 0 }} />
                ) : (
                  <Box
                    sx={{
                      width: 18,
                      height: 18,
                      borderRadius: '50%',
                      border: '2px solid',
                      borderColor: isYourTurn ? 'primary.contrastText' : 'divider',
                      flexShrink: 0,
                    }}
                  />
                )}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant='body2'
                    color={
                      approved ? 'text.primary' : rejectedAtThisStage ? 'error.main' : isYourTurn ? 'inherit' : 'text.secondary'
                    }
                    sx={{ fontWeight: isYourTurn ? 600 : 500 }}
                  >
                    {label}
                    {approved ? (
                      <>
                        {' · '}
                        <Typography component='span' variant='body2' color='text.secondary' sx={{ fontWeight: 400 }}>
                          {approverName || 'Unknown'} · {stageDec?.at ? dayjs(stageDec.at).format('MMM D, h:mm A') : ''}
                        </Typography>
                      </>
                    ) : rejectedAtThisStage ? (
                      <>
                        {' · '}
                        <Typography component='span' variant='body2' color='error.main' sx={{ fontWeight: 400 }}>
                          {approverName || 'Unknown'}
                          {stageDec?.at ? ` · ${dayjs(stageDec.at).format('MMM D, h:mm A')}` : ''}
                        </Typography>
                      </>
                    ) : (
                      <Typography
                        component='span'
                        variant='body2'
                        color={isYourTurn ? 'inherit' : 'text.secondary'}
                        sx={{
                          ml: 0.5,
                          fontStyle: isYourTurn ? 'normal' : 'italic',
                          fontWeight: isYourTurn ? 600 : 400,
                        }}
                      >
                        {isYourTurn ? ' — Your stage' : ' — Pending'}
                      </Typography>
                    )}
                  </Typography>
                </Box>
              </Box>
            )
          })}
        </Stack>
      </Box>
    )
  }

  const VehicleDecisionActions = () => {
    const rejectedStage = getRejectedStage(vehicleDecision)
    const conditionalStages = getConditionallyApprovedStages(vehicleDecision)
    const rejectedNotes = useMemo(() => {
      if (!rejectedStage) return []
      const dec = getStageDecision(vehicleDecision, rejectedStage)
      if (!dec?.note?.trim()) return []
      return [{ stage: STAGE_DISPLAY_NAMES[rejectedStage], note: dec.note.trim() }]
    }, [vehicleDecision, rejectedStage])
    const conditionalNotes = useMemo(() => {
      return conditionalStages
        .map(s => {
          const dec = getStageDecision(vehicleDecision, s)
          if (!dec?.note?.trim()) return null
          return { stage: STAGE_DISPLAY_NAMES[s], note: dec.note.trim() }
        })
        .filter((n): n is { stage: string; note: string } => n != null)
    }, [vehicleDecision, conditionalStages])

    const hasRejectionNotes = rejectedNotes.length > 0
    const hasConditionalNotes = conditionalNotes.length > 0
    const hasAnyNote = hasRejectionNotes || hasConditionalNotes
    const color =
      derivedStatus === 'rejected'
        ? 'error'
        : derivedStatus === 'conditionally_approved'
          ? 'warning'
          : derivedStatus === 'pending'
            ? 'default'
            : 'success'
    const labelColor =
      derivedStatus === 'rejected'
        ? 'error.dark'
        : derivedStatus === 'conditionally_approved'
          ? 'warning.dark'
          : derivedStatus === 'pending'
            ? 'text.secondary'
            : 'success.dark'

    const rejectionBoxSx = (theme: Theme) => ({
      mt: 1.5,
      p: 1.25,
      borderRadius: 1,
      border: '1px solid',
      borderColor: theme.palette.error.main,
      bgcolor: alpha(theme.palette.error.main, 0.1),
    })
    const conditionalBoxSx = (theme: Theme) => ({
      mt: 1.5,
      p: 1.25,
      borderRadius: 1,
      border: '1px solid',
      borderColor: theme.palette.warning.main,
      bgcolor: alpha(theme.palette.warning.main, 0.1),
    })

    return (
      <Box sx={{ mt: 1 }}>
        <ApprovalStagesRow />
        {vehicleDecision && (decisionLabel !== 'In progress' || hasAnyNote) && (
          <>
            {decisionLabel !== 'In progress' && !hasAnyNote && (
              <Typography variant='body2' sx={{ fontWeight: 600, color: labelColor, mt: 1.5 }}>
                {rejectedStage
                  ? `${rejectedStage.charAt(0).toUpperCase() + rejectedStage.slice(1)} rejected`
                  : decisionLabel}
              </Typography>
            )}
            {hasRejectionNotes && (
              <Box sx={rejectionBoxSx}>
                <Stack spacing={1}>
                  {rejectedNotes.map(({ stage, note }) => (
                    <Box key={stage}>
                      <Typography variant='body2' sx={{ fontWeight: 600, color: 'error.dark' }}>
                        {stage} rejected
                      </Typography>
                      <Typography
                        variant='body2'
                        sx={{ mt: 0.25, color: 'error.dark', fontWeight: 500, whiteSpace: 'pre-wrap' }}
                      >
                        {note}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}
            {hasConditionalNotes && (
              <Box sx={conditionalBoxSx}>
                <Stack spacing={1}>
                  {conditionalNotes.map(({ stage, note }) => (
                    <Box key={stage}>
                      <Typography variant='body2' sx={{ fontWeight: 600, color: 'warning.dark' }}>
                        {stage} conditionally approved
                      </Typography>
                      <Typography
                        variant='body2'
                        sx={{ mt: 0.25, color: 'warning.dark', fontWeight: 500, whiteSpace: 'pre-wrap' }}
                      >
                        {note}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}
          </>
        )}
        {canApprove && !hasPickupLocation && (
          <Typography variant='body2' color='warning.main' sx={{ fontWeight: 500, mt: 1.5 }}>
            Pickup location required to approve
          </Typography>
        )}
        {canApprove && (onApprove || onConditionalApprove || onReject) && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Stack
              direction={isMobile ? 'column' : 'row'}
              spacing={1}
              flexWrap='wrap'
              useFlexGap
              alignItems={isMobile ? 'stretch' : 'center'}
            >
              {!isMobile ? (
                <Typography
                  variant='caption'
                  color='text.secondary'
                  sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}
                >
                  Actions
                </Typography>
              ) : null}
              <Stack
                direction={isMobile ? 'column' : 'row'}
                spacing={0.75}
                flexWrap='wrap'
                useFlexGap
                sx={{ width: isMobile ? '100%' : 'auto' }}
              >
                <Button
                  size='small'
                  color='success'
                  variant='contained'
                  startIcon={<CheckCircle fontSize='small' />}
                  onClick={onApprove}
                  disabled={isUpdating || !hasPickupLocation}
                  fullWidth={isMobile}
                  sx={{ minHeight: 44 }}
                >
                  Approve
                </Button>
                <Button
                  size='small'
                  color='warning'
                  variant='outlined'
                  startIcon={<Warning fontSize='small' />}
                  onClick={onConditionalApprove}
                  disabled={isUpdating || !hasPickupLocation}
                  fullWidth={isMobile}
                  sx={{ minHeight: 44 }}
                >
                  Conditional
                </Button>
                <Button
                  size='small'
                  color='error'
                  variant='outlined'
                  startIcon={<Cancel fontSize='small' />}
                  onClick={onReject}
                  disabled={isUpdating}
                  fullWidth={isMobile}
                  sx={{ minHeight: 44 }}
                >
                  Reject
                </Button>
              </Stack>
            </Stack>
          </>
        )}
      </Box>
    )
  }

  const VehiclePhoto = () => {
    if (isLoadingPhoto) {
      return (
        <Box display='flex' justifyContent='center' alignItems='center' height='100%'>
          <CircularProgress sx={{ mt: 2 }} />
        </Box>
      )
    }

    const iconSrc = cachedIcons[vehicle?.type?.toLowerCase() as keyof typeof cachedIcons] || cachedIcons.default

    return (
      <>
        {photos && photos.length > 0 ? (
          <CardMedia
            component='img'
            srcSet={`${photos[0].url}?w=82&h=82&fit=crop&auto=format&dpr=2 2x`}
            src={`${photos[0].url}?w=82&h=82&fit=crop&auto=format`}
            alt={[vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' ')}
            loading='lazy'
            sx={{
              height: '100%',
              objectFit: 'cover',
            }}
          />
        ) : (
          <Box
            display='flex'
            justifyContent='center'
            alignItems='center'
            height='100%'
            sx={{ bgcolor: 'background.default' }}
          >
            <div
              title='No Photo Available'
              style={{
                width: '100%',
                height: '100%',
                backgroundImage: `url(${iconSrc})`,
                backgroundSize: 'contain',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                borderRadius: '4px',
                padding: '2px',
                mixBlendMode: 'multiply',
              }}
              role='img'
              aria-label={`${vehicle?.type || 'Default'} vehicle`}
            ></div>
          </Box>
        )}
      </>
    )
  }

  const mobileStatusChip =
    decisionLabel && decisionLabel !== 'In progress'
      ? { tone: vehicleDecisionStatusTone(derivedStatus), label: decisionLabel }
      : vehicle.status
        ? { tone: 'info' as StatusTone, label: vehicle.status }
        : undefined

  const mobileBuyerLabel =
    vehicle.sold_to_user?.name || vehicle.bought_from || vehicle.who || 'N/A'

  const mobileSellAmount =
    vehicle.optic_list_price != null ? formatPrice(vehicle.optic_list_price) : 'N/A'

  // Mobile view
  const MobileVehicleCard = () => {
    return (
      <Card
        sx={{
          mb: 0.1,
          border: '2px solid',
          borderColor: 'primary.main',
          borderRadius: 2,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 4,
            right: 4,
            zIndex: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            bgcolor: 'background.paper',
            boxShadow: 1,
            borderRadius: 1,
            px: 0.25,
          }}
        >
          <Tooltip title='View timeline'>
            <IconButton
              size='small'
              onClick={() => setTimelineDialogOpen(true)}
              aria-label='View timeline'
              sx={{
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <TimelineIcon sx={{ width: 22, height: 22, color: 'primary.main' }} />
            </IconButton>
          </Tooltip>
          <VehicleEditButton
            vehicle={vehicle}
            inventoryCollection={collectionName}
            size='small'
            tooltip='Edit vehicle'
            sx={{ color: 'primary.main' }}
          />
        </Box>

        <Stack spacing={0}>
          <Box sx={{ height: 140, position: 'relative', bgcolor: 'background.default' }}>
            <VehiclePhoto />
          </Box>

          <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, pr: 5 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant='subtitle1'
                  fontWeight='bold'
                  sx={{
                    lineHeight: 1.2,
                    fontSize: '0.95rem',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {[vehicle.year, vehicle.make, vehicle.model, vehicle.size].filter(Boolean).join(' ')}
                </Typography>
                <Typography variant='body2' color='text.secondary' sx={{ fontSize: '0.8125rem', mt: 0.5 }}>
                  Stock {vehicle.stock_number}
                </Typography>
                <Typography variant='body2' sx={{ fontSize: '0.8125rem', mt: 0.25 }}>
                  Buyer · {mobileBuyerLabel}
                </Typography>
                <Typography variant='body2' sx={{ fontSize: '0.875rem', fontWeight: 700, mt: 0.25 }}>
                  {mobileSellAmount}
                </Typography>
              </Box>
              {mobileStatusChip ? (
                <StatusChip tone={mobileStatusChip.tone} label={mobileStatusChip.label} dense sx={{ flexShrink: 0 }} />
              ) : null}
            </Box>

            {dealQualityInfo && vehicle.dealQuality ? (
              <StatusChip tone={dealQualityStatusTone(vehicle.dealQuality)} label={dealQualityInfo.label} dense />
            ) : null}

            <Stack spacing={0.75}>
              <Box display='flex' alignItems='center' gap={0.5}>
                <LocationOn sx={{ fontSize: '14px', color: 'text.secondary' }} />
                <Typography variant='body2' color='text.secondary' sx={{ fontSize: '0.8125rem' }}>
                  {vehicle.location || 'N/A'} · {formatDistance(vehicle.miles)} mi
                </Typography>
              </Box>

              <Box display='flex' alignItems='flex-start' gap={0.5}>
                <LocationOn sx={{ fontSize: '14px', color: 'text.secondary', mt: 0.2 }} />
                <Typography
                  variant='body2'
                  color={pickupLocationText ? 'text.secondary' : 'warning.main'}
                  sx={{ fontSize: '0.8125rem' }}
                >
                  Pickup · {pickupLocationText || 'Not set'}
                </Typography>
              </Box>

              {vehicle.truck_notes?.trim() && (
                <Box
                  sx={{
                    p: 1,
                    borderRadius: 1.5,
                    bgcolor: 'action.hover',
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography variant='caption' color='text.secondary' sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                    Truck notes
                  </Typography>
                  <Typography variant='body2' color='text.secondary' sx={{ fontSize: '0.8125rem', whiteSpace: 'pre-wrap' }}>
                    {vehicle.truck_notes.trim()}
                  </Typography>
                </Box>
              )}

              <Stack spacing={0.25}>
                <Typography variant='body2' color='text.primary' sx={{ fontSize: '0.8125rem', fontWeight: 'medium' }}>
                  Contact · {vehicle.who || 'N/A'}
                </Typography>
                <Typography variant='body2' color='text.secondary' sx={{ fontSize: '0.8125rem' }}>
                  {formatPhoneNumber(vehicle.phone_number)}
                </Typography>
                {vehicle.bought_from ? (
                  <Typography variant='body2' sx={{ fontSize: '0.8125rem' }}>
                    Bought · {vehicle.bought_from}
                  </Typography>
                ) : null}
                {vehicle.sold_to_user?.name ? (
                  <Typography variant='body2' sx={{ fontSize: '0.8125rem' }}>
                    Sold · {vehicle.sold_to_user.name}
                  </Typography>
                ) : null}
              </Stack>

              <Stack spacing={0.25}>
                {canViewPurchasePrice ? (
                  <Typography variant='body2' sx={{ fontSize: '0.8125rem', color: 'primary.main' }}>
                    Buy · {formatPrice(vehicle.seller_asking_price)}
                  </Typography>
                ) : null}
                {canViewProfit && vehicle.seller_asking_price != null && Number(vehicle.profit) !== 0 ? (
                  <Typography
                    variant='body2'
                    sx={{
                      fontSize: '0.8125rem',
                      fontWeight: 'medium',
                      color: Number(vehicle.profit) > 0 ? 'success.dark' : 'error.main',
                    }}
                  >
                    Profit · {formatPrice(vehicle.profit)}
                  </Typography>
                ) : null}
              </Stack>

              <Box sx={{ mt: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant='caption' color='text.secondary' sx={{ fontWeight: 600 }}>
                    Photos
                  </Typography>
                  <Tooltip title='Open all photos'>
                    <IconButton size='small' onClick={() => setPhotosDialogOpen(true)} aria-label='Open photos'>
                      <OpenInNewIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
                {allPhotos && allPhotos.length > 0 ? (
                  <ImageList sx={{ width: '100%', m: 0 }} cols={3} rowHeight={70} gap={4}>
                    {allPhotos.slice(0, 6).map((photo: { url: string; name?: string }) => (
                      <ImageListItem key={photo.url} sx={{ position: 'relative' }}>
                        <img
                          src={`${photo.url}?w=70&h=70&fit=crop&auto=format`}
                          alt={photo.name ?? 'Vehicle'}
                          loading='lazy'
                          style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 4 }}
                        />
                        <Tooltip title='Open in new tab'>
                          <IconButton
                            size='small'
                            onClick={e => {
                              e.stopPropagation()
                              window.open(photo.url, '_blank', 'noopener,noreferrer')
                            }}
                            aria-label='Open in new tab'
                            sx={{
                              position: 'absolute',
                              top: 2,
                              right: 2,
                              color: 'white',
                              bgcolor: 'rgba(0,0,0,0.4)',
                              padding: 0.25,
                              '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' },
                            }}
                          >
                            <OpenInNewIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      </ImageListItem>
                    ))}
                  </ImageList>
                ) : !isLoadingAllPhotos ? (
                  <Typography variant='caption' color='text.secondary'>
                    No photos
                  </Typography>
                ) : null}
              </Box>
            </Stack>
          </Box>

          {(vehicleDecision || canApprove) && (
            <Box sx={{ px: 1.5, pb: 1.5 }}>
              <VehicleDecisionActions />
            </Box>
          )}
        </Stack>
      </Card>
    )
  }

  // Desktop view
  return (
    <>
      {isMobile ? (
        <MobileVehicleCard />
      ) : (
        <Card
          sx={{
            minWidth: 275,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            color: 'text.primary',
          }}
        >
          <CardHeader
            sx={{ flexShrink: 0 }}
            title={
              <Box display='flex' justifyContent='space-between' alignItems='center'>
                <Typography
                  variant='h6'
                  sx={{
                    lineHeight: 1.2,
                    minHeight: '2.4em',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {[vehicle.year, vehicle.make, vehicle.model, vehicle.size].filter(Boolean).join(' ')}
                </Typography>
              </Box>
            }
            subheader={
              <Box display='flex' justifyContent='space-between' alignItems='center'>
                <Typography variant='body2'>Stock Number: {vehicle.stock_number}</Typography>
              </Box>
            }
            action={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                <Tooltip title='View timeline'>
                  <IconButton size='small' onClick={() => setTimelineDialogOpen(true)} aria-label='View timeline'>
                    <TimelineIcon sx={{ width: 24, height: 24, color: 'primary.main' }} />
                  </IconButton>
                </Tooltip>
                <VehicleEditButton
                  vehicle={vehicle}
                  inventoryCollection={collectionName}
                  size='small'
                  tooltip='Edit vehicle'
                  sx={{ color: 'primary.main' }}
                />
              </Box>
            }
          />
          <Box sx={{ height: 200, flexShrink: 0, position: 'relative' }}>
            <VehiclePhoto />
            {vehicle.status && (
              <Typography
                variant='caption'
                sx={{
                  position: 'absolute',
                  bottom: 4,
                  right: 4,
                  color: 'white',
                  backgroundColor: statusColor,
                  fontWeight: 'medium',
                  padding: '2px 6px',
                  borderRadius: '4px',
                  opacity: 0.9,
                }}
              >
                {vehicle.status}
              </Typography>
            )}
          </Box>
          <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', pt: 0 }}>
            <Stack spacing={2} sx={{ height: '100%' }}>
              {/* Vehicle details (incl. fuel, miles, deal quality) — two columns */}
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      borderLeft: '3px solid',
                      borderColor: 'primary.light',
                      pl: 1.5,
                      py: 0.5,
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant='caption' color='text.secondary' sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Vehicle
                    </Typography>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                      <Grid item xs={6}>
                        <Stack spacing={0.5}>
                          <Typography variant='body2'><Typography component='span' variant='caption' color='text.secondary'>VIN</Typography> {vehicle.vin}</Typography>
                          <Typography variant='body2'><Typography component='span' variant='caption' color='text.secondary'>Type</Typography> {vehicle.type}</Typography>
                          <Typography variant='body2'><Typography component='span' variant='caption' color='text.secondary'>Size</Typography> {vehicle.size || '—'}</Typography>
                        </Stack>
                      </Grid>
                      <Grid item xs={6}>
                        <Stack spacing={0.5}>
                          <Typography variant='body2'><Typography component='span' variant='caption' color='text.secondary'>Fuel</Typography> {vehicle.fuel}</Typography>
                          <Typography variant='body2'><Typography component='span' variant='caption' color='text.secondary'>Miles</Typography> {formatDistance(vehicle.miles)}</Typography>
                          <Typography variant='body2'><Typography component='span' variant='caption' color='text.secondary'>Who</Typography> {vehicle.who || 'N/A'}</Typography>
                          {dealQualityInfo && (
                            <Box>
                              <Typography component='span' variant='caption' color='text.secondary'>Deal quality </Typography>
                              <Chip size='small' color={dealQualityInfo.color} label={dealQualityInfo.label} sx={{ height: 20 }} />
                            </Box>
                          )}
                        </Stack>
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
              </Grid>

              {/* Pickup location — same Grid so left edge aligns with Vehicle */}
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      borderLeft: '3px solid',
                      borderColor: 'primary.main',
                      pl: 1.5,
                      py: 0.5,
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant='caption' color='text.secondary' sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Pickup location
                    </Typography>
                    <Typography variant='body2' color={pickupLocationText ? 'text.primary' : 'warning.dark'} sx={{ mt: 0.5 }}>
                      {pickupLocationText || 'Not set'}
                    </Typography>
                    {vehicle.pickup_location?.contact_name && (
                      <Typography variant='body2' color='text.secondary' sx={{ mt: 0.25, fontSize: '0.8125rem' }}>
                        {vehicle.pickup_location.contact_name}
                        {vehicle.pickup_location.contact_phone ? ` · ${formatPhoneNumber(vehicle.pickup_location.contact_phone)}` : ''}
                      </Typography>
                    )}
                  </Box>
                </Grid>
              </Grid>

              {/* Contact (if permitted) & Pricing */}
              <Grid container spacing={2}>
                {canViewPurchasePrice && (
                  <Grid item xs={6}>
                    <Box
                      sx={{
                        borderLeft: '3px solid',
                        borderColor: 'info.main',
                        pl: 1.5,
                        py: 0.5,
                        borderRadius: 1,
                      }}
                    >
                      <Typography variant='caption' color='text.secondary' sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Contact
                      </Typography>
                      <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                        <Typography variant='body2'>{vehicle.who}</Typography>
                        <Typography variant='body2' color='text.secondary' sx={{ fontSize: '0.8125rem' }}>{vehicle.location}</Typography>
                      </Stack>
                    </Box>
                  </Grid>
                )}
                <Grid item xs={canViewPurchasePrice ? 6 : 12}>
                  <Box
                    sx={{
                      borderLeft: '3px solid',
                      borderColor: 'success.light',
                      pl: 1.5,
                      py: 0.5,
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant='caption' color='text.secondary' sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Pricing
                    </Typography>
                    <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                      {canViewPurchasePrice && (
                        <Typography variant='body2'>
                          Buy {vehicle.seller_asking_price != null ? formatPrice(vehicle.seller_asking_price) : 'N/A'}
                        </Typography>
                      )}
                      <Typography variant='body2' sx={{ fontWeight: 600 }}>
                        Sell {vehicle.optic_list_price != null ? formatPrice(vehicle.optic_list_price) : 'N/A'}
                      </Typography>
                      {canViewProfit && vehicle.profit != null && (
                        <Typography variant='body2' color={Number(vehicle.profit) >= 0 ? 'success.main' : 'error.main'}>
                          Profit {formatPrice(vehicle.profit)}
                        </Typography>
                      )}
                    </Stack>
                  </Box>
                </Grid>
              </Grid>

              {/* Sale & Financing info */}
              {(vehicle.sold_to_user || vehicle.sold_by_user || vehicle.finance || vehicle.paid_for || vehicle.funded) && (
                <Grid container spacing={2}>
                  {(vehicle.sold_to_user || vehicle.sold_by_user) && (
                    <Grid item xs={12} sm={6}>
                      <Box
                        sx={{
                          borderLeft: '3px solid',
                          borderColor: 'info.main',
                          pl: 1.5,
                          py: 0.5,
                          borderRadius: 1,
                        }}
                      >
                        <Typography
                          variant='caption'
                          color='text.secondary'
                          sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}
                        >
                          Sale
                        </Typography>
                        <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                          {vehicle.sold_to_user?.name && (
                            <Typography variant='body2'>
                              Sold to {vehicle.sold_to_user.name}
                            </Typography>
                          )}
                          {vehicle.sold_by_user?.name && (
                            <Typography variant='body2' color='text.secondary'>
                              Sold by {vehicle.sold_by_user.name}
                            </Typography>
                          )}
                          {vehicle.sold_location && (
                            <Typography variant='body2' color='text.secondary'>
                              Location {vehicle.sold_location}
                            </Typography>
                          )}
                        </Stack>
                      </Box>
                    </Grid>
                  )}
                  {(vehicle.sold_date || vehicle.paid_for_date || vehicle.funded_date) && (
                    <Grid item xs={12} sm={6}>
                      <Box
                        sx={{
                          borderLeft: '3px solid',
                          borderColor: 'secondary.light',
                          pl: 1.5,
                          py: 0.5,
                          borderRadius: 1,
                        }}
                      >
                        <Typography
                          variant='caption'
                          color='text.secondary'
                          sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}
                        >
                          Dates
                        </Typography>
                        <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                          {vehicle.sold_date && (
                            <Typography variant='body2' color='text.secondary'>
                              Sold {formatDateTime(vehicle.sold_date)}
                            </Typography>
                          )}
                          {vehicle.paid_for_date && (
                            <Typography variant='body2' color='text.secondary'>
                              Paid for {formatDateTime(vehicle.paid_for_date)}
                            </Typography>
                          )}
                          {vehicle.funded_date && (
                            <Typography variant='body2' color='text.secondary'>
                              Funded {formatDateTime(vehicle.funded_date)}
                            </Typography>
                          )}
                        </Stack>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              )}

              {/* Truck notes — box style like approval stages */}
              {vehicle.truck_notes?.trim() && (
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 1.5,
                    bgcolor: 'action.hover',
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography
                    variant='caption'
                    color='text.secondary'
                    sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600, display: 'block', mb: 1 }}
                  >
                    Truck notes
                  </Typography>
                  <Typography variant='body2' sx={{ whiteSpace: 'pre-wrap' }}>
                    {vehicle.truck_notes.trim()}
                  </Typography>
                </Box>
              )}

              {/* Photos — 3 per row, taller; each has open-in-new-tab button */}
              <Grid container spacing={1.5}>
                <Grid item xs={12}>
                  <Box sx={{ position: 'relative' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0 }}>
                      <Typography variant='caption' color='text.secondary' sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Photos
                      </Typography>

                    </Box>
                    {isLoadingAllPhotos ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 2 }}>
                        <CircularProgress size={24} />
                      </Box>
                    ) : allPhotos && allPhotos.length > 0 ? (
                      <ImageList sx={{ width: '100%' }} cols={3} rowHeight={110}>
                        {allPhotos.map((photo: { url: string; name?: string; position?: string }) => (
                          <ImageListItem key={photo.url} sx={{ position: 'relative' }}>
                            <img
                              srcSet={`${photo.url}?w=110&h=110&fit=crop&auto=format&dpr=2 2x`}
                              src={`${photo.url}?w=110&h=110&fit=crop&auto=format`}
                              alt={photo.name ?? 'Vehicle'}
                              loading='lazy'
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                            {photo.position === 'main' && (
                              <Box
                                sx={{
                                  position: 'absolute',
                                  bottom: 2,
                                  right: 2,
                                  backgroundColor: 'primary.main',
                                  color: 'white',
                                  borderRadius: '4px',
                                  padding: '1px 4px',
                                  fontSize: '0.65rem',
                                  fontWeight: 'bold',
                                }}
                              >
                                MAIN
                              </Box>
                            )}
                            <Tooltip title='Open in new tab'>
                              <IconButton
                                size='small'
                                onClick={e => {
                                  e.stopPropagation()
                                  window.open(photo.url, '_blank', 'noopener,noreferrer')
                                }}
                                aria-label='Open photo in new tab'
                                sx={{
                                  position: 'absolute',
                                  top: 4,
                                  right: 4,
                                  color: 'white',
                                  bgcolor: 'rgba(0,0,0,0.4)',
                                  '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' },
                                }}
                              >
                                <OpenInNewIcon fontSize='small' />
                              </IconButton>
                            </Tooltip>
                          </ImageListItem>
                        ))}
                      </ImageList>
                    ) : (
                      <Typography variant='body2' color='text.secondary'>
                        No photos
                      </Typography>
                    )}
                  </Box>
                </Grid>
              </Grid>

              {(vehicleDecision || canApprove) && (
                <Box>
                  <VehicleDecisionActions />
                </Box>
              )}
            </Stack>
          </CardContent>
        </Card>
      )}
      <TimelineDialog
        open={timelineDialogOpen}
        onClose={() => setTimelineDialogOpen(false)}
        vehicle={vehicle}
      />
      <ManagePhotosDialog
        open={photosDialogOpen}
        onClose={() => setPhotosDialogOpen(false)}
        collectionName={collectionName}
        vehicle={vehicle}
      />
    </>
  )
}

export default DealApprovalVehicleCard
